/**
 * Port of the analyses in tools/graph.sh (the fingerprint-fragmentation measurement).
 * Input is the on-device graph.json shape: {states[], edges[]}.
 */
export const shortFp = (fp) => fp.slice(0, 8);
export const tailOf = (s) => s.split("/").pop() ?? s;
export const shortActivity = (a) => (a ? a.split(".").pop() ?? a : "?");
export function activityHistogram(g) {
    const acc = new Map();
    let unknown = 0;
    for (const s of g.states) {
        const pkg = s.pkg ?? "";
        const activity = s.activity ?? "?";
        const key = `${pkg} ${activity}`;
        const cur = acc.get(key) ?? { pkg, activity, visits: 0, states: 0 };
        cur.visits += s.visits ?? 0;
        cur.states += 1;
        acc.set(key, cur);
        if (activity === "?" || activity === "")
            unknown += s.visits ?? 0;
    }
    return { activities: [...acc.values()].sort((a, b) => b.visits - a.visits), unknownVisits: unknown };
}
export function edgeInstability(g) {
    const tos = new Map();
    for (const e of g.edges) {
        if (e.removed)
            continue;
        const key = `${e.fromFp} ${e.action}`;
        const m = tos.get(key) ?? new Map();
        m.set(e.toFp, (m.get(e.toFp) ?? 0) + (e.count ?? 0));
        tos.set(key, m);
    }
    const out = [];
    for (const [key, m] of tos) {
        if (m.size <= 1)
            continue;
        const [fromFp, action] = key.split(" ");
        out.push({
            fromFp,
            action,
            landings: [...m.entries()].map(([toFp, count]) => ({ toFp, count })).sort((a, b) => b.count - a.count),
        });
    }
    return out.sort((a, b) => b.landings.length - a.landings.length);
}
export function jaccard(a, b) {
    const sa = new Set(a);
    const sb = new Set(b);
    if (sa.size === 0 && sb.size === 0)
        return 0;
    let inter = 0;
    for (const x of sa)
        if (sb.has(x))
            inter++;
    const union = sa.size + sb.size - inter;
    return union === 0 ? 0 : inter / union;
}
export function stateAliasing(g, threshold = 0.7) {
    const outActions = new Map();
    for (const e of g.edges) {
        if (e.removed)
            continue;
        const s = outActions.get(e.fromFp) ?? new Set();
        s.add(e.action);
        outActions.set(e.fromFp, s);
    }
    const groups = new Map();
    for (const s of g.states) {
        const key = `${s.pkg ?? ""} ${s.activity ?? ""}`;
        const arr = groups.get(key) ?? [];
        arr.push(s);
        groups.set(key, arr);
    }
    const out = [];
    for (const [key, grp] of groups) {
        const [pkg, activity] = key.split(" ");
        for (let i = 0; i < grp.length; i++) {
            for (let j = i + 1; j < grp.length; j++) {
                const si = grp[i];
                const sj = grp[j];
                const idsI = si.sampleIds ?? [];
                const idsJ = sj.sampleIds ?? [];
                const overlap = jaccard(idsI, idsJ);
                if (overlap < threshold)
                    continue;
                const setI = new Set(idsI);
                const setJ = new Set(idsJ);
                const onlyA = idsI.filter((x) => !setJ.has(x)).map(tailOf).sort();
                const onlyB = idsJ.filter((x) => !setI.has(x)).map(tailOf).sort();
                const ai = [...(outActions.get(si.fp) ?? [])].sort();
                const aj = [...(outActions.get(sj.fp) ?? [])].sort();
                const same = ai.length > 0 && ai.length === aj.length && ai.every((x, k) => x === aj[k]);
                out.push({
                    pkg,
                    activity,
                    a: si.fp,
                    b: sj.fp,
                    overlap,
                    onlyA,
                    onlyB,
                    actionsA: ai,
                    actionsB: aj,
                    likelyFragmentation: same,
                });
            }
        }
    }
    return out.sort((a, b) => b.overlap - a.overlap);
}
export function analyzeGraph(g) {
    const { activities, unknownVisits } = activityHistogram(g);
    const instability = edgeInstability(g);
    const aliasing = stateAliasing(g);
    return {
        stateCount: g.states.length,
        edgeCount: g.edges.filter((e) => !e.removed).length,
        activities,
        unknownActivityVisits: unknownVisits,
        instability,
        aliasing,
        fragmentation: aliasing.some((p) => p.likelyFragmentation),
    };
}
//# sourceMappingURL=graph-analysis.js.map